import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, throwError, timer } from 'rxjs';
import { catchError, retry, switchMap } from 'rxjs/operators';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';

/**
 * Cliente del rail de salida **USD→PEN** (endpoints `peru/cotizar`,
 * `peru/remesa-pe`). Espejo self-contained de `OthersPayoutsService` acotado a
 * Perú: NO toca el service de Colombia (aislamiento total). Misma robustez:
 * idToken (no accessToken), `X-Skip-Error-Interceptor` para 409 accionables,
 * envío idempotente con reintento transitorio por `requestId`.
 */

export interface RemesaPeRequest {
  montoUsd: number;
  beneficiarioId: string;
  /** PEN que vio el cliente al confirmar (price-check). */
  expectedRecibePen?: number;
  /** Idempotencia (UUID v4 por intención de envío). */
  requestId?: string;
  /** Override del velocity check: el cliente confirmó el duplicado. */
  confirmarDuplicado?: boolean;
}

export interface RemesaPeResponse {
  txId: number;
  estado: string; // POR_PROCESAR
  rail: 'PE';
  montoUsd: number;
  recibePen: number;
  businessDays: number | null;
}

export interface CotizacionPe {
  disponible: boolean;
  rail?: 'PE';
  montoUsd?: number;
  tasaPen?: number;
  /**
   * Tasa de REFERENCIA pública del rail (`fiat.pe.usd`, hoy ≈3,3833). Es la que
   * se muestra; `tasaPen` es la operativa con la que el backend calcula y, en
   * modo ESPEJO, queda ~1,5 % por debajo. `null` si el feed no está vivo.
   */
  tasaReferenciaPen?: number | null;
  comisionPen?: number;
  recibePen?: number;
  businessDays?: number | null;
}

/** La tasa cambió entre el preview y el envío. */
export interface ValoresCambiaronPe {
  cambioValores: true;
  mensaje: string;
  recibePen: number;
  tasaPen: number;
  businessDays: number | null;
}

/** Errores accionables del envío (mismo contrato que CO). */
export interface RemesaPeErrorAccionable {
  tipo: 'REMITENTE_DATOS_INCOMPLETOS' | 'DATOS_INCOMPLETOS' | 'POSIBLE_DUPLICADO';
  mensaje: string;
  faltantes?: any[];
  beneficiarioId?: string;
  variante?: string;
  previa?: {
    txId: number;
    estado: string;
    enVuelo: boolean;
    montoUsd?: number;
    recibePen?: number;
    creadaAt?: string;
  };
}

// ─── VES→PEN (usuario VE paga con saldo VES) ───

export interface CotizacionVesPe {
  disponible: boolean;
  motivo?: string;
  rail?: 'PE';
  montoVes?: number;
  recibePen?: number;
  tasaPenVes?: number;
  /**
   * Tasa de REFERENCIA PEN por VES (bruta, antes de comisión). Sólo alimenta el
   * desglose; `recibePen` NO depende de ella. `null` = mostrar sólo la efectiva.
   */
  tasaReferenciaPenVes?: number | null;
  minVes?: number;
  maxVes?: number;
  bajoMinimo?: boolean;
  excedeMaximo?: boolean;
  activo?: boolean;
  businessDays?: number | null;
}

export interface RemesaVesPeRequest {
  montoVes: number;
  beneficiarioId: string;
  expectedRecibePen?: number;
  requestId?: string;
  confirmarDuplicado?: boolean;
}

export interface RemesaVesPeResponse {
  txId: number;
  estado: string;
  rail: 'PE';
  montoVes: number;
  recibePen: number;
  tasaPenVes: number;
  businessDays: number | null;
}

// ─── CLP→PEN (usuario CL paga con saldo CLP) ───

export interface CotizacionClpPe {
  disponible: boolean;
  motivo?: string;
  rail?: 'PE';
  montoClp?: number;
  recibePen?: number;
  tasaPenClp?: number;
  /**
   * Tasa de REFERENCIA PEN por CLP (bruta, antes de comisión). Sólo alimenta el
   * desglose; `recibePen` NO depende de ella. `null` = mostrar sólo la efectiva.
   */
  tasaReferenciaPenClp?: number | null;
  minClp?: number;
  maxClp?: number;
  bajoMinimo?: boolean;
  excedeMaximo?: boolean;
  activo?: boolean;
  businessDays?: number | null;
}

export interface RemesaClpPeRequest {
  montoClp: number;
  beneficiarioId: string;
  expectedRecibePen?: number;
  requestId?: string;
  confirmarDuplicado?: boolean;
}

export interface RemesaClpPeResponse {
  txId: number;
  estado: string;
  rail: 'PE';
  montoClp: number;
  recibePen: number;
  tasaPenClp: number;
  businessDays: number | null;
}

@Injectable({ providedIn: 'root' })
export class PeruPayoutsService {
  private baseUrl = `${environment.apiNest}peru`;
  private readonly REINTENTOS_TRANSITORIOS = 2;

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  /** Cotización real (Modelo A / ESPEJO) para mostrar al cliente — concuerda con el envío. */
  cotizar(montoUsd: number): Observable<CotizacionPe> {
    return this.postConRetry401<CotizacionPe>('/cotizar', { montoUsd }).pipe(
      catchError((err: HttpErrorResponse) => this.handleError(err)),
    );
  }

  enviarRemesa(body: RemesaPeRequest): Observable<RemesaPeResponse> {
    return this.postRemesaIdempotente<RemesaPeResponse>('/remesa-pe', body).pipe(
      catchError((err: HttpErrorResponse) => {
        // La tasa cambió entre el preview y el envío → no se creó nada.
        if (err?.status === 409 && err?.error?.codigo === 'VALORES_CAMBIARON') {
          return throwError(
            () =>
              ({
                cambioValores: true,
                mensaje: err.error.mensaje,
                recibePen: err.error.recibePen,
                tasaPen: err.error.tasaPen,
                businessDays: err.error.businessDays ?? null,
              } as ValoresCambiaronPe),
          );
        }
        // 409 accionables: NO se creó nada.
        const codigo = err?.status === 409 ? err?.error?.codigo : null;
        if (
          codigo === 'REMITENTE_DATOS_INCOMPLETOS' ||
          codigo === 'DATOS_INCOMPLETOS' ||
          codigo === 'POSIBLE_DUPLICADO'
        ) {
          return throwError(
            () =>
              ({
                tipo: codigo,
                mensaje: err.error.mensaje || 'Revisá los datos e intentá nuevamente.',
                faltantes: err.error.faltantes,
                beneficiarioId: err.error.beneficiarioId,
                variante: err.error.variante,
                previa: err.error.previa,
              } as RemesaPeErrorAccionable),
          );
        }
        // 5xx / timeout / red persistente (agotados los reintentos idempotentes).
        if (err?.status === 0 || (err?.status >= 500 && err?.status <= 599)) {
          return throwError(
            () => 'No se pudo completar el envío. Volvé a intentarlo en unos minutos.',
          );
        }
        return this.handleError(err);
      }),
    );
  }

  /** Cotización cliente VES→PEN (paga en VES). */
  cotizarVes(montoVes: number): Observable<CotizacionVesPe> {
    return this.postConRetry401<CotizacionVesPe>('/ves/cotizar', { montoVes }).pipe(
      catchError((err: HttpErrorResponse) => this.handleError(err)),
    );
  }

  enviarRemesaVes(body: RemesaVesPeRequest): Observable<RemesaVesPeResponse> {
    return this.postRemesaIdempotente<RemesaVesPeResponse>('/ves/remesa', body).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err?.status === 409 && err?.error?.codigo === 'VALORES_CAMBIARON') {
          return throwError(
            () =>
              ({
                cambioValores: true,
                mensaje: err.error.mensaje,
                recibePen: err.error.recibePen,
                tasaPen: err.error.tasaPen,
                businessDays: err.error.businessDays ?? null,
              } as ValoresCambiaronPe),
          );
        }
        const codigo = err?.status === 409 ? err?.error?.codigo : null;
        if (
          codigo === 'REMITENTE_DATOS_INCOMPLETOS' ||
          codigo === 'DATOS_INCOMPLETOS' ||
          codigo === 'POSIBLE_DUPLICADO'
        ) {
          return throwError(
            () =>
              ({
                tipo: codigo,
                mensaje: err.error.mensaje || 'Revisá los datos e intentá nuevamente.',
                faltantes: err.error.faltantes,
                beneficiarioId: err.error.beneficiarioId,
                variante: err.error.variante,
                previa: err.error.previa,
              } as RemesaPeErrorAccionable),
          );
        }
        if (err?.status === 0 || (err?.status >= 500 && err?.status <= 599)) {
          return throwError(
            () => 'No se pudo completar el envío. Volvé a intentarlo en unos minutos.',
          );
        }
        return this.handleError(err);
      }),
    );
  }

  /** Cotización cliente CLP→PEN (paga en CLP). */
  cotizarClp(montoClp: number): Observable<CotizacionClpPe> {
    return this.postConRetry401<CotizacionClpPe>('/clp/cotizar', { montoClp }).pipe(
      catchError((err: HttpErrorResponse) => this.handleError(err)),
    );
  }

  enviarRemesaClp(body: RemesaClpPeRequest): Observable<RemesaClpPeResponse> {
    return this.postRemesaIdempotente<RemesaClpPeResponse>('/clp/remesa', body).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err?.status === 409 && err?.error?.codigo === 'VALORES_CAMBIARON') {
          return throwError(
            () =>
              ({
                cambioValores: true,
                mensaje: err.error.mensaje,
                recibePen: err.error.recibePen,
                tasaPen: err.error.tasaPen,
                businessDays: err.error.businessDays ?? null,
              } as ValoresCambiaronPe),
          );
        }
        const codigo = err?.status === 409 ? err?.error?.codigo : null;
        if (
          codigo === 'REMITENTE_DATOS_INCOMPLETOS' ||
          codigo === 'DATOS_INCOMPLETOS' ||
          codigo === 'POSIBLE_DUPLICADO'
        ) {
          return throwError(
            () =>
              ({
                tipo: codigo,
                mensaje: err.error.mensaje || 'Revisá los datos e intentá nuevamente.',
                faltantes: err.error.faltantes,
                beneficiarioId: err.error.beneficiarioId,
                variante: err.error.variante,
                previa: err.error.previa,
              } as RemesaPeErrorAccionable),
          );
        }
        if (err?.status === 0 || (err?.status >= 500 && err?.status <= 599)) {
          return throwError(
            () => 'No se pudo completar el envío. Volvé a intentarlo en unos minutos.',
          );
        }
        return this.handleError(err);
      }),
    );
  }

  // ─── Infra (idToken + idempotencia), espejo de OthersPayoutsService ───

  private postRemesaIdempotente<T>(path: string, body: unknown): Observable<T> {
    return this.postConRetry401<T>(path, body).pipe(
      retry({
        count: this.REINTENTOS_TRANSITORIOS,
        delay: (err: HttpErrorResponse) => {
          const transitorio =
            err?.status === 0 || (err?.status >= 500 && err?.status <= 599);
          if (!transitorio) return throwError(() => err);
          return timer(1200);
        },
      }),
    );
  }

  private postConRetry401<T>(path: string, body: unknown): Observable<T> {
    const doPost = () =>
      from(this.getHeaders()).pipe(
        switchMap((headers) =>
          this.http.post<T>(`${this.baseUrl}${path}`, body, { headers }),
        ),
      );
    return doPost().pipe(
      catchError((err: HttpErrorResponse) => {
        if (err?.status !== 401) return throwError(() => err);
        this.cognitoService.clearCache();
        return doPost();
      }),
    );
  }

  private async getHeaders(): Promise<HttpHeaders> {
    // idToken (NO accessToken): el backend valida con tokenUse:'id'.
    let session = await this.cognitoService.getSession();
    let token = session?.idToken?.jwtToken;
    if (token && this.isJwtNearExpiry(token, 60)) {
      if (typeof (this.cognitoService as any).clearCache === 'function') {
        (this.cognitoService as any).clearCache();
      }
      session = await this.cognitoService.getSession();
      token = session?.idToken?.jwtToken;
    }
    if (!token) throw new Error('No se pudo obtener el token de sesión');
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${token.trim()}`)
      // Escape hatch del ErrorInterceptor: necesitamos el HttpErrorResponse
      // completo (codigo/previa/faltantes) para los 409 accionables.
      .set('X-Skip-Error-Interceptor', 'true');
  }

  private isJwtNearExpiry(jwt: string, thresholdSec: number): boolean {
    try {
      const parts = jwt.split('.');
      if (parts.length !== 3) return false;
      const payload = JSON.parse(
        atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
      );
      if (!payload?.exp) return false;
      const nowSec = Math.floor(Date.now() / 1000);
      return payload.exp - nowSec < thresholdSec;
    } catch {
      return false;
    }
  }

  private handleError(err: HttpErrorResponse): Observable<never> {
    const msg =
      err?.error?.mensaje ||
      err?.error?.message ||
      'No se pudo completar la operación. Intentá nuevamente.';
    return throwError(() => msg);
  }
}
